import React, { forwardRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';
import { getStarIcon } from '../../utils/starRating';

// 🔄 SVGs moved to Cloudflare R2
const Feefo =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/feefo.svg';
const Google =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/google.svg';
const HelloPeter =
  'https://assets.askhippo.co.za/Pages/Rating%20Providers/hellopeter.svg';

const GridVDivider = ({ sx = {} }) => (
  <Box
    sx={{
      width: '1px',
      backgroundColor: '#E0E0E0',
      alignSelf: 'stretch',
      minHeight: '1px',
      ...sx,
    }}
  />
);

const ProviderTileComponent = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      bestDealContentReady = true,
      industryCategory,
    },
    ref
  ) => {
    const { LogoURL } = data;
    const bestDealBannerText = industryCategory
      ? `Recommended for ${industryCategory}`
      : 'Recommended for Your Business';

    // --- Ratings (defensive parsing) ---
    const reviewData = [
      {
        name: 'Feefo',
        score: parseFloat(
          (data.FeefoScore ?? data.FeefoStars ?? '0')
            .toString()
            .replace(',', '.')
        ),
        logo: Feefo,
      },
      {
        name: 'Google',
        score: parseFloat(
          (data.GoogleScore ?? data.GoogleStars ?? '0')
            .toString()
            .replace(',', '.')
        ),
        logo: Google,
      },
      {
        name: 'HelloPeter',
        score: parseFloat(
          (data.HelloPeterScore ?? data.HelloPeterStars ?? '0')
            .toString()
            .replace(',', '.')
        ),
        logo: HelloPeter,
      },
    ];

    const isBestDealLoading = data.bestDeal && !bestDealContentReady;

    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      return onBuyOnline(evt, { action: 'buy-online', data });
    };

    return (
      <Box
        ref={ref}
        sx={{
          width: '1199px',
          minHeight: data.bestDeal && initialLoadDone ? '288px' : '0px',
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '2px solid #2DC8CB' : 'none',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* ===== Best Deal Banner ===== */}
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              px: '18px',
              py: '12px',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: '120%',
                letterSpacing: '-0.03em',
                color: '#FFFFFF',
              }}
            >
              {bestDealContentReady ? (
                bestDealBannerText
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* ===== Main Grid ===== */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              '16px 210px 16px 1px 14px 700px 20px 1px 16px auto',
            // '16px 210px 16px 1px 17px 710px 0px 1px 16px auto',
            alignItems: 'stretch',
            py: data.bestDeal && initialLoadDone ? '12px' : '16px',
            mt: '16px',
            mb: '16px',
          }}
        >
          <Box />

          {/* Logo */}
          <Box
            sx={{
              minHeight: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: '12px',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={LogoURL}
                alt={data.BrandName.replace('.co.za', '')}
                style={{
                  maxWidth: '100%',
                  maxHeight: '76px',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          <Box />
          <GridVDivider />
          <Box />

          {/* Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '24px',
                  color: '#1E354E',
                }}
              >
                {data.BrandName.replace('.co.za', '')}
              </Typography>
            </SkeletonLoaderWrapper>

            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  mt: '-6px',
                  color: '#738289',
                }}
              >
                {data.BrandText}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  mt: '-6px',
                  color: '#162F3B',
                }}
              >
                Assist Benefits:
              </Typography>
              <Box
                sx={{
                  pl: '12px',
                  mt: '-2px',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  color: '#738289',

                  /* Reset incoming html */
                  '& p, & div, & ul': { margin: 0, padding: 0 },
                  '& br': { display: 'none' },

                  /* Remove default bullets */
                  '& li': {
                    listStyle: 'none',
                    position: 'relative',
                    paddingLeft: '22px',
                  },

                  /* Add tick */
                  '& li::before': {
                    content: '"✓"',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    color: '#1E354E',
                    fontWeight: 700,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: data.CoverFor ?? '' }}
              />
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  mt: '-6px',
                  color: '#162F3B',
                }}
              >
                Cashback:
              </Typography>
              <Box
                sx={{
                  pl: '12px',
                  mt: '-2px',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  color: '#738289',

                  '& p, & div, & ul': { margin: 0, padding: 0 },
                  '& br': { display: 'none' },

                  '& li': {
                    listStyle: 'none',
                    position: 'relative',
                    paddingLeft: '22px',
                  },

                  '& li::before': {
                    content: '"✓"',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    color: '#1E354E',
                    fontWeight: 700,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: data.CashBack ?? '' }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          <Box />
          <GridVDivider />
          <Box />

          {/* Premium + CTA */}
          <Box sx={{ minHeight: '100px', height: '100%' }}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                pr: '20px',
                mt: '-16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '4px',
                  mt: data.bestDeal && initialLoadDone ? '-40px' : '0px',
                }}
              >
                {/* Reviews */}
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    lineHeight: '28px',
                    color: '#1E354E',
                    mb: '8px',
                  }}
                >
                  Reviews
                </Typography>

                {reviewData.map((r, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      mb: '12px',
                    }}
                  >
                    <img
                      src={r.logo}
                      alt={`${r.name} logo`}
                      style={{
                        maxWidth: '95px',
                        maxHeight: '53px',
                        objectFit: 'contain',
                      }}
                    />

                    <Box sx={{ display: 'flex', gap: '4px' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <img
                          key={star}
                          src={getStarIcon(star, r.score)}
                          alt="star"
                          style={{ width: '18px', height: '18px' }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}

                {/* CTA — Now for ALL providers */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '16px',
                  }}
                >
                  <Button
                    onClick={
                      data.OnlinePremium &&
                      data.OnlinePremium < data.TotalPremium
                        ? handleBuyOnline
                        : handleSelectQuote
                    }
                    disabled={isBestDealLoading}
                    sx={{
                      borderRadius: '18px',
                      gap: '8px',
                      px: '16px',
                      py: '8px',
                      backgroundColor: '#FF0074',
                      border: '1px solid #FF0074',
                      boxShadow: '0px 1px 2px 0px #1018280D',
                      textTransform: 'none',
                      minWidth: 0,
                      '&:hover': { backgroundColor: '#e60067' },
                      ...(isBestDealLoading && {
                        backgroundColor: '#D5D5D5 !important',
                        borderColor: '#D5D5D5 !important',
                        color: '#7A7A7A !important',
                        cursor: 'not-allowed',
                        boxShadow: 'none',
                      }),
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: '24px',
                        letterSpacing: '-0.02em',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      I&apos;m Interested
                      <span
                        className="material-symbols-rounded"
                        style={{ fontSize: '1.5rem' }}
                      >
                        double_arrow
                      </span>
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

ProviderTileComponent.displayName = 'ProviderTileComponent';
export default ProviderTileComponent;
